import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { useConfiguration } from "../../../../api/configurations/use-configuration";
import { useEndExperience } from "../../../../api/experience-actions/end-experience";
import { usePauseExperience } from "../../../../api/experience-actions/pause-experience";
import { useStartExperience } from "../../../../api/experience-actions/start-experience";
import { useIsAdmin } from "../../../../api/user-auths/is-admin";
import { getDialog, setDialog } from "../../../../store/slices/global";
import {
  endPriceTest,
  intelligemsWizardPriceTest,
} from "../../../../utils/linkSheet";
import openNewTab from "../../../../utils/openNewTab";
import { VariationButtons } from "../VariationButtons";

const PREFIX = "PriceUpdaterDialog";

const classes = {
  root: `${PREFIX}-root`,
  dialogTitle: `${PREFIX}-dialogTitle`,
  dialogText1: `${PREFIX}-dialogText1`,
  dialogText2: `${PREFIX}-dialogText2`,
  articleLink: `${PREFIX}-articleLink`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.root}`]: {
    transition: "0.2s",
    borderRadius: "4px",
    border: `1px solid ${theme.palette.gray.light}`,
    "&:hover, &:focus": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  [`& .${classes.dialogTitle}`]: {
    fontSize: "22px",
    fontWeight: 600,
  },
  [`& .${classes.dialogText1}`]: {
    marginTop: "15px",
    marginBottom: "15px",
    color: theme.palette.grey[900],
  },
  [`& .${classes.dialogText2}`]: {
    marginBottom: 15,
  },
  [`& .${classes.articleLink}`]: {
    textDecoration: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
}));

interface PriceUpdaterDialogProps {
  currExperience: ExperienceDto;
  noRedirect?: boolean;
}

const startExpTextAdmin =
  "If you choose to manually update your prices you will be responsible for updating your shopify prices before and after a test. For more information, see our help docs";
const startExpTextMember =
  "Intelligems will update each product's price in Shopify to the highest price being tested. For more information, see our help docs";

const endExpText =
  "Select the test group you would like to roll out. If you choose to manually update your prices, pausing/ending this experiment will set your prices to the highest priced test group. For more information, see our help docs";

export const PriceUpdaterDialog = ({
  currExperience,
  noRedirect,
}: PriceUpdaterDialogProps) => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const priceUpdaterDialogProps = useSelector(getDialog("priceUpdater"));
  const downloadPriceCSVEnabled = useConfiguration("enable_price_csv_download");
  const startExperience = useStartExperience();
  const endExperience = useEndExperience(noRedirect, currExperience.id);
  const pauseExperience = usePauseExperience();
  const isExperienceActive = currExperience.status === "started";

  const [selectedTestGroup, setSelectedTestGroup] = useState<
    string | undefined
  >(undefined);

  const handleExperienceTestStart = (updatePrices: boolean) => {
    startExperience.mutate({
      body: {
        variationId: updatePrices ? null : undefined,
        updatePricesToVariation: updatePrices,
      },
      pathParams: {
        name: currExperience.name!,
        pausedAtTs: currExperience.pausedAtTs ?? 0,
        experienceId: currExperience.id!,
        action: "start",
      },
      queryParams: undefined,
    });
    dispatch(
      setDialog({
        dialog: "priceUpdater",
        open: false,
        props: { action: "none" },
      })
    );
  };

  const handleExperienceTestAction = (updatePrices: boolean) => {
    if (priceUpdaterDialogProps.props.action === "pause") {
      pauseExperience.mutate({
        body: {
          variationId: selectedTestGroup,
          updatePricesToVariation: updatePrices,
        },
        pathParams: {
          name: currExperience.name!,
          experienceId: currExperience.id!,
          action: "pause",
        },
        queryParams: undefined,
      });
    } else if (priceUpdaterDialogProps.props.action === "end") {
      endExperience.mutate({
        body: {
          variationId: selectedTestGroup,
          updatePricesToVariation: updatePrices,
        },
        pathParams: {
          name: currExperience.name!,
          experienceId: currExperience.id!,
          action: "end",
        },
        queryParams: undefined,
      });
    }

    dispatch(setDialog({ dialog: "priceUpdater", open: false, props: "none" }));
  };

  const selectedCard = (id: string): boolean => {
    return selectedTestGroup === id;
  };

  return (
    <StyledDialog
      open={priceUpdaterDialogProps.isOpen}
      aria-labelledby="pricing-dialog"
      aria-describedby="pricing-dialog"
      onClose={() => {
        dispatch(
          setDialog({
            dialog: "priceUpdater",
            open: false,
            props: { action: "none" },
          })
        );
      }}
      maxWidth={"xs"}
      fullWidth={true}
      PaperProps={{
        style: { borderRadius: "15px", padding: "30px 20px 30px 20px" },
      }}
    >
      <Grid
        container
        direction={"column"}
        alignItems={"center"}
        textAlign={"center"}
      >
        <DialogTitle
          sx={{ width: "350px" }}
          id="dialogBox-title"
          className={classes.dialogTitle}
        >
          Do you want the Intelligems Wizard to update your Shopify prices?
        </DialogTitle>
        <DialogContent sx={{ width: "350px" }}>
          <IgTypography>
            {isExperienceActive
              ? endExpText
              : isAdmin
                ? startExpTextAdmin
                : startExpTextMember}
            <IgTypography
              onClick={() => {
                isExperienceActive
                  ? openNewTab(endPriceTest)
                  : openNewTab(intelligemsWizardPriceTest);
              }}
              igStyle={"link"}
            >
              {" "}
              here.
            </IgTypography>
          </IgTypography>
          {(priceUpdaterDialogProps.props.action === "end" ||
            priceUpdaterDialogProps.props.action === "pause") && (
            <>
              <DialogContentText
                className={classes.dialogText1}
                id="dialogBox-text-1"
              >
                Which prices would you like to roll out?
              </DialogContentText>
              <VariationButtons
                selectedTestGroup={selectedTestGroup}
                setSelectedTestGroup={setSelectedTestGroup}
                selectedCard={selectedCard}
                rootClass={classes.root}
                variations={currExperience.variations}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Grid container direction={"column"}>
            <IgButton
              loading={startExperience.isPending}
              variant={"contained"}
              disabled={isExperienceActive && selectedTestGroup === undefined}
              onClick={() => {
                !isExperienceActive &&
                priceUpdaterDialogProps.props.action === "start"
                  ? handleExperienceTestStart(true)
                  : handleExperienceTestAction(true);
              }}
            >
              {`Yes, Update My Prices and ${
                priceUpdaterDialogProps.props.action === "start"
                  ? "Start"
                  : priceUpdaterDialogProps.props.action === "pause"
                    ? "Pause"
                    : "End"
              } Test`}
            </IgButton>
            {isAdmin || downloadPriceCSVEnabled?.value ? (
              <IgButton
                loading={startExperience.isPending}
                onClick={() => {
                  !isExperienceActive &&
                  priceUpdaterDialogProps.props.action === "start"
                    ? handleExperienceTestStart(false)
                    : handleExperienceTestAction(false);
                }}
              >
                {}
                No, I'll Update My Prices
              </IgButton>
            ) : (
              <IgButton
                onClick={() => {
                  dispatch(
                    setDialog({
                      dialog: "priceUpdater",
                      open: false,
                      props: { action: "none" },
                    })
                  );
                }}
              >
                Cancel
              </IgButton>
            )}
          </Grid>
        </DialogActions>
      </Grid>
    </StyledDialog>
  );
};
